import {Buffer} from 'buffer'
import {uuid} from 'uuidv4'

import {DeserializedFile, UploadedFile} from './constants'
import {Jurisdiction} from './countries/constants'

export const serializeFile = async (file: File): Promise<Record<any, any>> =>
  new Promise((resolve, reject) => {
    const {name, type, size} = file
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      resolve({name, type, size, data: reader.result})
    }
    reader.onerror = () => reject(reader.error)
  })

export const deserializeFile = ({data, ...rest}: UploadedFile): DeserializedFile => ({
  ...rest,
  data: deserializeData(data),
})

export const deserializeData = (data: string) =>
  Buffer.from(data.split(';base64,').pop() as string, 'base64')

export const serializeValues = async (data: any): Promise<any> => {
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return await Promise.all(data.map((value) => serializeValues(value)))
    }
    if (data instanceof File) {
      return await serializeFile(data)
    }

    const serialisedValues = await Promise.all(
      Object.entries(data).map(async ([key, value]) => {
        const serialisedValue = await serializeValues(value)
        return [key, serialisedValue]
      }),
    )

    return Object.fromEntries(serialisedValues)
  }
  return data
}

// shared by the renewal and ownership-transfer forms (and any future jurisdiction-scoped trademark
// form), which all keep a `jurisdictions` array in sync with a `trademark_details` array grouped
// by jurisdiction - only the shape of a single trademark detail row differs between flows
export const addJurisdictionToTrademarkForm = <
  T extends {jurisdiction: Jurisdiction; uuid?: string},
>(
  jurisdiction: Jurisdiction,
  jurisdictions: Jurisdiction[],
  trademarkDetails: T[],
  createTrademarkDetail: (jurisdiction: Jurisdiction, uuid: string) => T,
) => ({
  jurisdictions: [...jurisdictions, jurisdiction],
  trademarkDetails: [...trademarkDetails, createTrademarkDetail(jurisdiction, uuid())],
})

export const removeJurisdictionFromTrademarkForm = <T extends {jurisdiction: Jurisdiction}>(
  jurisdiction: Jurisdiction,
  jurisdictions: Jurisdiction[],
  trademarkDetails: T[],
) => ({
  jurisdictions: jurisdictions.filter((selected) => selected !== jurisdiction),
  trademarkDetails: trademarkDetails.filter((trademark) => trademark.jurisdiction !== jurisdiction),
})
